import { countOf, fmtMoney, type GbValueGridEntry } from './types.js';

// ---------------------------------------------------------------------------
// The reserve
// ---------------------------------------------------------------------------
// Nothing here says which values were DEALT. That was a straight leak:
// the "in play" set was identical to the secret deal, so you could read
// off exactly which twelve of the twenty were out there before a single
// ball turned over. Undrawn, dealt-but-hidden and the never-revealed
// round-two sit-outs all look the same, deliberately.
//
// What round two CAN say — and what this panel used to hide — is which
// values are guaranteed back in the pool. Every round-one ball is turned
// face up at the round-end reveal and every one of them returns, so that
// set is already public knowledge. Only the couple drawn fresh from the
// leftovers is secret, and the grid says how many without naming one.

type Tone = 'removed' | 'faceUp' | 'returns' | 'unknown';

const TONE_CLASS: Record<Tone, string> = {
  removed: 'border-[#e02a1f]/60 bg-[#e02a1f]/10 text-ec-bone-dim line-through',
  faceUp: 'border-ec-go/60 bg-ec-go/10 text-ec-face',
  returns: 'border-ec-brass/50 bg-ec-brass/5 text-ec-face',
  unknown: 'border-ec-line bg-ec-panel text-ec-bone-dim'
};

const TONE_TITLE: Record<Tone, string> = {
  removed: 'Revealed and out of play',
  faceUp: 'Face up right now',
  returns: 'Seen in round one — back in the pool for certain',
  unknown: 'Not seen — could be anywhere, or nowhere'
};

function toneOf(entry: GbValueGridEntry): Tone {
  if (entry.removed) return 'removed';
  if (entry.faceUp) return 'faceUp';
  if (entry.returnsFromR1) return 'returns';
  return 'unknown';
}

export function ValueGrid({
  entries,
  killers,
  round,
  newDraws = 0,
  newDrawCandidates = 0,
  compact = false
}: {
  entries: GbValueGridEntry[];
  killers: { inPool: number; faceUpFront: number };
  round: 1 | 2;
  newDraws?: number;
  newDrawCandidates?: number;
  compact?: boolean;
}) {
  const seen = entries.filter((entry) => entry.faceUp || entry.removed).length;

  // Round two splits into two genuinely different groups. Round one has
  // only one — nothing has returned yet, so the split would be a lie.
  const returning = round === 2 ? entries.filter((entry) => entry.returnsFromR1) : [];
  const leftovers = round === 2 ? entries.filter((entry) => !entry.returnsFromR1) : [];

  return (
    <div className="flex min-h-0 flex-col gap-2">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="font-display text-xs uppercase tracking-wide text-ec-bone-dim">
          The reserve
        </h3>
        <span className="font-display text-[0.6rem] uppercase tracking-wide text-ec-bone-dim">
          {seen}/{entries.length} seen
        </span>
      </div>

      <p className="font-serif text-[0.65rem] text-ec-bone-dim">
        Round {round} · <span className="text-[#e02a1f]">{countOf(killers.inPool, 'killer')}</span>{' '}
        in play
        {killers.faceUpFront > 0 && `, ${killers.faceUpFront} face up`}
      </p>

      {round === 2 ? (
        <>
          <Group
            title="Back from round one"
            note={`${returning.length} values — all of them returned, guaranteed in the pool`}
            entries={returning}
            compact={compact}
          />
          <Group
            title="The rest of the reserve"
            note={
              newDrawCandidates > 0
                ? `${newDraws} of these ${newDrawCandidates} entered play — nobody knows which`
                : 'none of these entered play'
            }
            entries={leftovers}
            compact={compact}
          />
        </>
      ) : (
        <Group entries={entries} compact={compact} />
      )}

      <Legend showReturns={round === 2} />
    </div>
  );
}

function Group({
  title,
  note,
  entries,
  compact
}: {
  title?: string;
  note?: string;
  entries: GbValueGridEntry[];
  compact: boolean;
}) {
  if (entries.length === 0) return null;
  return (
    <div className="flex flex-col gap-1">
      {title && (
        <p className="font-display text-[0.55rem] uppercase tracking-widest text-ec-bone-dim">
          {title}
          {note && <span className="ml-1 normal-case tracking-normal opacity-80">· {note}</span>}
        </p>
      )}
      <div
        className={`grid gap-1 ${
          compact ? 'grid-cols-4 sm:grid-cols-5' : 'grid-cols-3 sm:grid-cols-5'
        }`}
      >
        {entries.map((entry) => {
          const tone = toneOf(entry);
          return (
            <div
              key={entry.value}
              title={
                tone === 'faceUp' ? `Face up (${entry.faceUp})` : TONE_TITLE[tone]
              }
              className={`flex items-center justify-between gap-1 rounded border px-1.5 py-1 font-serif text-xs tabular-nums ${TONE_CLASS[tone]}`}
            >
              <span className="truncate">{fmtMoney(entry.value)}</span>
              {entry.fromInflationList && (
                <span
                  title="Inflation-list value — only ever appears in round two"
                  className="font-display text-[0.5rem] uppercase text-ec-bone-dim"
                >
                  r2
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Legend({ showReturns }: { showReturns: boolean }) {
  return (
    <ul className="flex flex-wrap gap-x-3 gap-y-1 border-t border-ec-line/60 pt-1.5">
      <Key tone="unknown" label="not seen" />
      {showReturns && <Key tone="returns" label="back in the pool" />}
      <Key tone="faceUp" label="face up" />
      <Key tone="removed" label="gone" />
    </ul>
  );
}

function Key({ tone, label }: { tone: Tone; label: string }) {
  return (
    <li className="flex items-center gap-1">
      <span className={`h-2.5 w-2.5 shrink-0 rounded-sm border ${TONE_CLASS[tone]}`} />
      <span className="font-display text-[0.55rem] uppercase tracking-wide text-ec-bone-dim">
        {label}
      </span>
    </li>
  );
}
